import { logger } from "~/lib/logger/logger";
import { env } from "~/config/env";
import type { CMSLink } from "~/types/cms";

/**
 * Maps collection slugs to their route prefixes.
 * Must stay in sync with cms/src/utilities/generatePreviewPath.ts
 */
const COLLECTION_PREFIX_MAP: Record<string, string> = {
  articles: "/artikel",
  podcasts: "/podcast",
  "podcast-episodes": "/podcast/episode",
};

/**
 * Landing pages use a separate frontend URL.
 * This is configured via VITE_LANDING_PAGE_URL environment variable.
 */
const LANDING_PAGE_URL = env.landingPageUrl;

/**
 * Maps CMS page slugs to their actual frontend routes.
 * Pages have dedicated static routes in the frontend.
 */
const PAGE_SLUG_TO_ROUTE_MAP: Record<string, string> = {
  home: "/",
  datenschutz: "/datenschutz",
  empfang: "/empfang",
  genderhinweise: "/genderhinweise",
  impressum: "/impressum",
  karriere: "/karriere",
  kontakt: "/kontakt",
  team: "/team",
};

/**
 * Reference value structure from CMS
 */
export interface CMSReferenceValue {
  id: string;
  slug?: string;
  identifier?: string;
  pageType?: "sender" | "stream";
  channelType?: "sender" | "stream";
  title?: string;
  [key: string]: unknown;
}

/**
 * Reference structure from CMS links
 */
export interface CMSReference {
  relationTo: string;
  value: string | CMSReferenceValue;
}

/**
 * Resolves a CMS reference to a URL path.
 * Determines the correct route based on the collection type (relationTo).
 *
 * @param reference - The CMS reference object containing relationTo and value
 * @returns The resolved URL path, or "#" if unresolvable
 */
export function resolveReferenceUrl(reference: CMSReference | undefined | null): string {
  if (!reference) return "#";

  // If value is just an ID string (not populated), we can't resolve it
  if (typeof reference.value === "string") {
    logger.debug("Reference not populated (ID only):", reference.value);
    return "#";
  }

  const { relationTo, value } = reference;

  // Handle documents - resolve to file download URL (documents have no slug)
  if (relationTo === "documents") {
    const fileUrl = (value as Record<string, unknown>).url as string | undefined;
    if (fileUrl) {
      return fileUrl.startsWith("http") ? fileUrl : `${env.cmsApiUrl}${fileUrl}`;
    }
    logger.warn("Document reference has no file URL:", reference);
    return "#";
  }

  // Get slug from either `slug` or `identifier` field (channel-pages use identifier)
  const slug = value.slug || value.identifier;

  if (!slug) {
    logger.debug("Reference has no slug or identifier:", reference);
    return "#";
  }

  // Handle pages collection - uses dedicated routes or slug directly
  if (relationTo === "pages") {
    const route = PAGE_SLUG_TO_ROUTE_MAP[slug];
    if (route) return route;
    // Fallback for unknown page slugs
    return `/${slug}`;
  }

  // Handle channel-pages - uses /sender or /stream prefix based on pageType or channelType
  if (relationTo === "channel-pages") {
    const pageType = value.pageType || value.channelType || "sender";
    return `/${pageType}/${slug}`;
  }

  // Handle landing-pages - uses separate frontend URL
  if (relationTo === "landing-pages") {
    const route = slug === "home" ? "/" : `/${slug}`;
    return `${LANDING_PAGE_URL}${route}`;
  }

  // Handle other collections with prefix mapping
  const prefix = COLLECTION_PREFIX_MAP[relationTo];
  if (prefix) {
    return `${prefix}/${slug}`;
  }

  // Fallback: use slug directly (for unknown collections)
  logger.debug("Unknown collection, using slug directly:", relationTo);
  return `/${slug}`;
}

/**
 * Resolves a CMS link to a URL
 *
 * IMPORTANT: This function strictly respects the `type` field:
 * - If type is "custom", it ONLY uses the `url` field (ignores `reference`)
 * - If type is "reference", it ONLY uses the `reference` field (ignores `url`)
 *
 * This prevents issues when a link is changed from "custom" to "reference" in the CMS
 * and the old `url` value remains populated but should be ignored.
 *
 * @param link - The CMS link object
 * @returns The resolved URL
 */
export function resolveCMSLink(link: CMSLink): string {
  if (link.type === "custom" && link.url) {
    return link.url;
  }

  if (link.type === "reference" && link.reference) {
    return resolveReferenceUrl(link.reference as CMSReference);
  }

  logger.warn("Unknown link type or missing data:", link);
  return "#";
}
